import { InvitationAPIs, OrganizationAPIs, UserAPIs } from '@/api';
import type { Invitation } from '@/api/types/invitation';
import type { User } from '@/api/types/user';
import { UserType } from '@/api/types/user';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, useCallback, useRef, type FC } from 'react';
import type { ChangeEvent } from 'react';
import { Button, ButtonSize, ButtonType } from './button';
import { CustomToast } from './custom-toast';
import { InitialsCircle } from './initials-circle';
import { Menu } from './menu';
import { Modal } from './modal';
import { TextField, TextFieldType } from './text-field';
import { UploadIcon } from './upload-icon';

interface AdminControlsModalProps {
    onAccountUpdated?: () => void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

export const AdminControlsModal: FC<AdminControlsModalProps> = ({ onAccountUpdated, isOpen, setIsOpen }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [organizationName, setOrganizationName] = useState<string>('');
    const [inviteEmail, setInviteEmail] = useState<string>('');
    const [picture, setPicture] = useState<string>('');
    const [pickedPicture, setPickedPicture] = useState<File | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
    const [selectedInviteType, setSelectedInviteType] = useState<UserType | null>(null);
    const [invitations, setInvitations] = useState<Invitation[]>([]);

    const fetchUser = useCallback(async () => {
        try {
            const user = await UserAPIs.getCurrentUser();
            setUser(user);
            setOrganizationName(user.organization.name);
            setPicture(user.organization.logo);
        } catch (error) {
            CustomToast.error('Unable to fetch user data');
        }
    }, []);

    const fetchOrganizationUsers = useCallback(async () => {
        try {
            const users = await UserAPIs.getOrganizationUsers();
            setOrganizationUsers(users.results);
        } catch (error) {
            CustomToast.error('Unable to fetch organization users');
        }
    }, []);

    const fetchInvitations = useCallback(async () => {
        try {
            setInvitations((await InvitationAPIs.getInvitations()).results);
        } catch (error) {
            CustomToast.error('Unable to fetch invitations');
        }
    }, []);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event?.target?.files?.[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/png'];
            if (validTypes.includes(file.type)) {
                setPickedPicture(file);
            } else {
                CustomToast.error('Please select a JPEG or PNG file');
            }
        }
    };

    const updateOrganization = useCallback(async () => {
        setIsLoading(true);
        try {
            await OrganizationAPIs.updateOrganization(organizationName, pickedPicture, () => {
                onAccountUpdated?.();
                CustomToast.success('Account updated successfully');
            });
        } catch (error) {
            CustomToast.error('Unable to update account');
        } finally {
            setIsLoading(false);
        }
    }, [onAccountUpdated, organizationName, pickedPicture]);

    const updateUserType = useCallback(
        async (userId: number, type: UserType) => {
            try {
                await UserAPIs.updateUserType(userId, type);
                fetchOrganizationUsers();
            } catch (error) {
                CustomToast.error('Unable to update user type');
            }
        },
        [fetchOrganizationUsers],
    );

    const updateInvitationType = useCallback(
        async (invitationId: number, type: UserType) => {
            try {
                await InvitationAPIs.updateInvitationType(invitationId, type);
                fetchInvitations();
            } catch (error) {
                CustomToast.error('Unable to update user type');
            }
        },
        [fetchInvitations],
    );

    const deleteUser = useCallback(
        async (userId: number) => {
            try {
                await UserAPIs.deleteUser(userId);
                fetchOrganizationUsers();
            } catch (error) {
                CustomToast.error('Unable to delete user');
            }
        },
        [fetchOrganizationUsers],
    );

    const sendInvite = useCallback(async () => {
        try {
            setIsLoading(true);
            await InvitationAPIs.createInvitation(inviteEmail, selectedInviteType || '');
            await fetchInvitations();
            setSelectedInviteType(null);
            setInviteEmail('');
        } catch (error) {
            CustomToast.error('Unable to send invite');
        } finally {
            setIsLoading(false);
        }
    }, [fetchInvitations, inviteEmail, selectedInviteType]);

    const resendInvite = useCallback(
        async (invitationId: number) => {
            try {
                setIsLoading(true);
                await InvitationAPIs.resendInvitation(invitationId);
                await fetchInvitations();
                CustomToast.success('Invite resent successfully');
            } catch (error) {
                CustomToast.error('Unable to resend invite');
            } finally {
                setIsLoading(false);
            }
        },
        [fetchInvitations],
    );

    const revokeInvite = useCallback(
        async (invitationId: number) => {
            try {
                setIsLoading(true);
                await InvitationAPIs.deleteInvitation(invitationId);
                await fetchInvitations();
                CustomToast.success('Invite revoked successfully');
            } catch (error) {
                CustomToast.error('Unable to revoke invite');
            } finally {
                setIsLoading(false);
            }
        },
        [fetchInvitations],
    );

    const closeModal = () => {
        setIsOpen(false);
        setPickedPicture(null);
        setSelectedInviteType(null);
        setInviteEmail('');
    };

    useEffect(() => {
        fetchUser();
        fetchOrganizationUsers();
        fetchInvitations();
    }, [fetchUser, fetchOrganizationUsers, fetchInvitations]);

    const getInvitationTile = (invitation: Invitation) => {
        return (
            <div className="flex flex-row items-center space-between w-full p-2.5">
                <div className="flex flex-row items-center gap-3 w-full">
                    <div className="border-[1px] border-blue-40 rounded-full w-9 h-9" />
                    <p className="text-sm text-gray-60">{invitation.email}</p>
                    <p className="text-sm text-gray-60">•</p>
                    <p className={`text-sm ${invitation.isExpired ? 'text-red-30' : 'text-gray-60'}`}>
                        {invitation.isExpired ? 'Expired' : 'Invited'}
                    </p>
                </div>
                <Menu
                    items={[
                        ...(invitation.userType !== UserType.ADMIN
                            ? [
                                {
                                    name: 'Admin',
                                    id: 'inv-admin',
                                    onClick: () => updateInvitationType(invitation.id, UserType.ADMIN),
                                },
                            ]
                            : []),
                        ...(invitation.userType !== UserType.DESIGNER
                            ? [
                                {
                                    name: 'Designer',
                                    id: 'inv-designer',
                                    onClick: () => updateInvitationType(invitation.id, UserType.DESIGNER),
                                },
                            ]
                            : []),
                        ...(invitation.userType !== UserType.MEMBER
                            ? [
                                {
                                    name: 'Member',
                                    id: 'inv-member',
                                    onClick: () => updateInvitationType(invitation.id, UserType.MEMBER),
                                },
                            ]
                            : []),
                        {
                            name: 'Resend',
                            id: 'inv-opt-resend',
                            onClick: () => resendInvite(invitation.id),
                            color: 'red-50',
                        },
                        {
                            name: 'Revoke',
                            id: 'inv-opt-revoke',
                            onClick: () => revokeInvite(invitation.id),
                            color: 'red-50',
                        },
                    ]}
                    icon={
                        <div className="flex flex-row gap-2 items-center w-full min-w-32 justify-end">
                            <p className="text-sm text-blue-30 capitalize">{invitation.userType}</p>
                            <ChevronDownIcon className="text-blue-30 h-4 w-4" />
                        </div>
                    }
                />
            </div>
        );
    };

    const getMemberTile = (user: User) => {
        return (
            <div className="flex flex-row items-center space-between w-full p-2.5">
                <div className="flex flex-row items-center gap-3 w-full">
                    <InitialsCircle firstName={user.firstName} lastName={user.lastName} />
                    <div className="flex flex-col">
                        <p className="text-sm text-white">
                            {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-gray-110">{user.email}</p>
                    </div>
                </div>
                <Menu
                    items={[
                        ...(user.type !== UserType.ADMIN
                            ? [
                                {
                                    name: 'Admin',
                                    id: 'mem-admin',
                                    onClick: () => updateUserType(user.id, UserType.ADMIN),
                                },
                            ]
                            : []),
                        ...(user.type !== UserType.DESIGNER
                            ? [
                                {
                                    name: 'Designer',
                                    id: 'mem-designer',
                                    onClick: () => updateUserType(user.id, UserType.DESIGNER),
                                },
                            ]
                            : []),
                        ...(user.type !== UserType.MEMBER
                            ? [
                                {
                                    name: 'Member',
                                    id: 'mem-member',
                                    onClick: () => updateUserType(user.id, UserType.MEMBER),
                                },
                            ]
                            : []),
                        { name: 'Remove User', id: 'opt-remove', onClick: () => deleteUser(user.id), color: 'red-50' },
                    ]}
                    icon={
                        <div className="flex flex-row gap-2 items-center w-full min-w-32 justify-end">
                            <p className="text-sm text-blue-30 capitalize">{user.type}</p>
                            <ChevronDownIcon className="text-blue-30 h-4 w-4" />
                        </div>
                    }
                />
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen}>
            <div className="flex flex-col absolute min-w-96 w-[45vw] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                <div className="flex flex-row p-3 justify-between items-center">
                    <p className="text-xl text-white">Admin Controls</p>
                    <XMarkIcon
                        className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                        onClick={() => setIsOpen(false)}
                    />
                </div>
                <hr className="bg-gray-100 border-gray-100 w-full" />
                <div className="flex flex-row items-center">
                    <div className="flex flex-col p-4 w-full gap-2">
                        <p className="ml-1 text-white text-sm">Organization Name</p>
                        <TextField
                            id="organization-name"
                            required={true}
                            type={TextFieldType.Text}
                            value={organizationName}
                            placeholder="Organization Name"
                            onChange={(value) => {
                                setOrganizationName(value);
                            }}
                        />
                        <input
                            id="logo"
                            type="file"
                            accept="image/jpeg, image/png"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            multiple={false}
                            style={{ display: 'none' }}
                        />
                        <p className="ml-1 text-white text-sm">Invite Members</p>
                        <div className="flex flex-row gap-2 bg-gray-100 rounded-lg items-center">
                            <TextField
                                id="email"
                                className="w-full"
                                required={true}
                                type={TextFieldType.Email}
                                value={inviteEmail}
                                placeholder="Email"
                                onChange={(value) => {
                                    setInviteEmail(value);
                                }}
                            />
                            <div>
                                <Menu
                                    items={[
                                        {
                                            name: 'Admin',
                                            id: 'inv-admin',
                                            onClick: () => setSelectedInviteType(UserType.ADMIN),
                                        },
                                        {
                                            name: 'Designer',
                                            id: 'inv-designer',
                                            onClick: () => setSelectedInviteType(UserType.DESIGNER),
                                        },
                                        {
                                            name: 'Member',
                                            id: 'inv-member',
                                            onClick: () => setSelectedInviteType(UserType.MEMBER),
                                        },
                                    ]}
                                    icon={
                                        <div className="flex flex-row gap-2 items-center w-full min-w-32 justify-end p-2">
                                            <p className="text-sm text-white capitalize">
                                                {selectedInviteType ? selectedInviteType : 'Select Role'}
                                            </p>
                                            <ChevronDownIcon className="text-white h-4 w-4" />
                                        </div>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 px-3 items-center">
                        <div className="relative w-[72px] h-[72px] rounded-full overflow-hidden bg-gray-100 group">
                            {(picture || pickedPicture) && (
                                <img
                                    src={pickedPicture ? URL.createObjectURL(pickedPicture) : picture}
                                    alt="logo"
                                    className="w-full h-full object-cover"
                                />
                            )}
                            <button
                                type="button"
                                className="absolute inset-0"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <div
                                    className={`absolute inset-0 ${picture || pickedPicture ? 'bg-black bg-opacity-80 opacity-0' : ''} flex items-center justify-center transition-opacity duration-300 group-hover:opacity-100`}
                                >
                                    <UploadIcon className="h-5 w-5 stroke-white" />
                                </div>
                            </button>
                        </div>
                        <p className="text-xs text-gray-50">Company Icon</p>
                        <Button
                            title="Send Invite"
                            className="w-32"
                            buttonType={ButtonType.SECONDARY}
                            buttonSize={ButtonSize.LARGE}
                            disabled={isLoading || !inviteEmail || !selectedInviteType}
                            onClick={() => sendInvite()}
                        />
                    </div>
                </div>
                <div className="flex flex-col w-full">
                    <p className="text-sm text-white px-4 py-2">Members</p>
                    <div className="max-h-56 overflow-auto no-scrollbar w-full">
                        <div className="flex flex-col w-full">
                            {invitations.map((invitation, index) => {
                                const addSeparator = index !== organizationUsers.length - 1;
                                return (
                                    <div key={invitation.id} className="flex flex-col w-full">
                                        {getInvitationTile(invitation)}
                                        {addSeparator && <hr className="bg-gray-140 border-gray-140 w-full mt-1" />}
                                    </div>
                                );
                            })}
                            {organizationUsers.map((user, index) => {
                                const addSeparator = index !== organizationUsers.length - 1;
                                return (
                                    <div key={user.id} className="flex flex-col w-full">
                                        {getMemberTile(user)}
                                        {addSeparator && <hr className="bg-gray-140 border-gray-140 w-full mt-1" />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <hr className="bg-gray-100 border-gray-100 w-full mt-2" />
                <div className="w-full flex flex-row justify-end gap-3 p-6">
                    <Button
                        title="Cancel"
                        className="hover:bg-gray-160"
                        buttonType={ButtonType.SECONDARY}
                        buttonSize={ButtonSize.LARGE}
                        disabled={isLoading}
                        onClick={() => closeModal()}
                    />
                    <Button
                        title="Done"
                        buttonType={ButtonType.PRIMARY}
                        buttonSize={ButtonSize.LARGE}
                        disabled={isLoading}
                        onClick={async () => {
                            await updateOrganization();
                            closeModal();
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
};
